import './utils/Date';
import '@/webpack.tweaks.frontend';
import {
  createApp,
  defineAsyncComponent,
  computed,
  onBeforeMount,
  onMounted,
  onBeforeUnmount,
  ref,
  provide,
  nextTick,
} from 'vue';
import ConfigInterface from '@/interfaces/ConfigInterface';
import ListSectionInterface from '@/interfaces/ListSectionInterface';
import BaseGridRow from '@/components/BaseGridRow.vue';
import BaseGridCol from '@/components/BaseGridCol.vue';
import BaseGridWrap from '@/components/BaseGridWrap.vue';
import IoRouterLink from '@/components/IoRouterLink.vue';
import BaseLoader from '@/components/BaseLoader.vue';
import IoHeadline from '@/components/Headline.vue';
import IoHero from '@/components/Hero.vue';
import LazyBackgroundMedia from '@/components/LazyBackgroundMedia.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Equalize from '@/utils/Equalize';
import BaseHeader from '@/components/BaseHeader.vue';
import AppFooter from '@/components/AppFooter.vue';
import IoSection from '@/components/Section.vue';
import IoContainer from '@/components/Container.vue';
import IoText from '@/components/Text.vue';
import BaseSpacer from '@/components/BaseSpacer.vue';
import Enlargement from '@/components/Enlargement.vue';
import { canUseWebP } from '@/helpers';
import { initCustomBreakpointHandler, onBreakpointChanged } from '@/utils/viewport';
import store from './store';
import router from './router';
import '@/scss/app.scss';

const PageError = defineAsyncComponent({
  loader: () => import(/* webpackChunkName: "chunks/error-pages" */ '@/components/PageError.vue'),
  loadingComponent: BaseLoader,
});

const MediaSection = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/MediaSection.vue'));
const BaseButton = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/BaseButton.vue'));

const IoImageSpacer = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/spacer" */ '@/components/ImageSpacer.vue'));

const IoCard = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/card" */ '@/components/Card.vue'));
const IoMediaCard = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/card" */ '@/components/MediaCard.vue'));
const BlogCard = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/card" */ '@/components/BlogCard.vue'));

const IoResponsiveImage = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/responsive-image" */ '@/components/ResponsiveImage.vue'));

const IoFaqs = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/faqs" */ '@/components/Faqs.vue'));
const IoList = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/content-sections" */ '@/components/List.vue'));
const IoTooltip = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/content-sections" */ '@/components/Tooltip.vue'));
const IoCustomerFeedback = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/customer-feedback" */ '@/components/CustomerFeedback.vue'));

const PowerLabelings = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/charts" */ '@/components/PowerLabelings.vue'));
const BaseRating = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/charts" */ '@/components/BaseRating.vue'));

const BaseEqualizer = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/equalizer" */ '@/components/BaseEqualizer.vue'));
const BaseEqualizerElement = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/equalizer" */ '@/components/BaseEqualizerElement.vue'));
const BaseEqualizerElementGroup = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/equalizer" */ '@/components/BaseEqualizerElementGroup.vue'));
const BaseEqualizerElementPart = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/equalizer" */ '@/components/BaseEqualizerElementPart.vue'));

const BaseTabs = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/tabs" */ '@/components/BaseTabs.vue'));
const BaseTabsTab = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/tabs" */ '@/components/BaseTabsTab.vue'));

const MediaCardSlide = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/media-slider" */ '@/components/MediaCardSlide.vue'));
const MediaCardSlider = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/media-slider" */ '@/components/MediaCardSlider.vue'));
const BlogSlider = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/blog-slider" */ '@/components/BlogSlider.vue'));
const CardSlider = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/slider" */ '@/components/CardSlider.vue'));
const ProductOverviewSlider = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/slider" */ '@/components/product/ProductOverviewSlider.vue'));

const CompanyCertificates = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/certificates" */ '@/components/CompanyCertificates.vue'));
const BlogOverview = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/blog-overview" */ '@/components/BlogOverview.vue'));
const JobOverview = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/job-overview" */ '@/components/JobOverview.vue'));

const Disruptor = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/hero" */ '@/components/Disruptor.vue'));
const PageBanner = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/hero" */ '@/components/PageBanner.vue'));

const HeroSlider = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/hero-slider" */ '@/components/HeroSlider.vue'));
const HeroSlide = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/hero-slider" */ '@/components/HeroSlide.vue'));

const CircledIcon = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/circled-icon" */ '@/components/CircledIcon.vue'));

const ProcessLine = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/process" */ '@/components/ProcessLine.vue'));
const ProcessItem = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/process" */ '@/components/ProcessItem.vue'));

const BaseCalculator = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/calculator" */ '@/components/BaseCalculator.vue'));

const StepAddress = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/order-pages" */ '@/components/order/StepAddress.vue'));
const StepConnection = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/order-pages" */ '@/components/order/StepConnection.vue'));
const StepPayment = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/order-pages" */ '@/components/order/StepPayment.vue'));
const StepOverview = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/order-pages" */ '@/components/order/StepOverview.vue'));
const InputSection = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/order-pages" */ '@/components/order/InputSection.vue'));

const IoCardContact = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/contact-forms" */ '@/components/CardContact.vue'));
const IoFormContactCallback = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/contact-forms" */ '@/components/contact/CallbackForm.vue'));
const IoFormContactEmail = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/contact-forms" */ '@/components/contact/EmailForm.vue'));

const BaseFixedAside = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/fixed-aside" */ '@/components/aside/BaseFixedAside.vue'));
const ScrollTopButton = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/fixed-aside" */ '@/components/ScrollTopButton.vue'));

const FaIcon = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/fa-icon" */ '@/components/FaIcon.vue'));

const IconAndroid = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconAndroid.vue'));
const IconTree = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconTree.vue'));
const IconApple = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconApple.vue'));
const IconCallback = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconCallback.vue'));
const IconFacebook = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconFacebook.vue'));
const IconInstagram = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconInstagram.vue'));
const IconLiveChat = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconLiveChat.vue'));
const IconTwitter = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconTwitter.vue'));
const IconVideoConsulting = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconVideoConsulting.vue'));
const IconWhatsapp = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconWhatsapp.vue'));
const IconYoutube = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconYoutube.vue'));
const IconContact = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconContact.vue'));
const IconServiceApp = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconServiceApp.vue'));

const IoWave = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/wave" */ '@/components/IoWave.vue'));
const IconWave = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/wave" */ '@/components/icons/IconWave.vue'));
const IconWaveFlip = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/wave" */ '@/components/icons/IconWaveFlip.vue'));
const IconWaveNegativ = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/wave" */ '@/components/icons/IconWaveNegativ.vue'));
const IconWaveNegativFlip = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/wave" */ '@/components/icons/IconWaveNegativFlip.vue'));
const IconWaveDouble = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/wave" */ '@/components/icons/IconWaveDouble.vue'));
const IconWaveDoubleFlip = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/wave" */ '@/components/icons/IconWaveDoubleFlip.vue'));
const IconWaveDoubleNegativ = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/wave" */ '@/components/icons/IconWaveDoubleNegativ.vue'));
const IconWaveDoubleNegativFlip = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/wave" */ '@/components/icons/IconWaveDoubleNegativFlip.vue'));
const IconWaveDoubleCard = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/wave" */ '@/components/icons/IconWaveDoubleCard.vue'));

const FormContractCancellation = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/forms" */ '@/components/forms/FormContractCancellation.vue'));
const FormNewsletter = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/forms" */ '@/components/forms/FormNewsletter.vue'));
const FormRelocation = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/forms" */ '@/components/forms/FormRelocation.vue'));
const FormSponsoring = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/forms" */ '@/components/forms/FormSponsoring.vue'));

const SectionNewsletter = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/newsletter" */ '@/components/SectionNewsletter.vue'));

const IframeFacade = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/iframes" */ '@/components/iFrameFacade.vue'));

declare global {
  interface Window {
    siteUrl: string,
    ioappConfig: ConfigInterface,
    currentListSection: ListSectionInterface,
    getUcConsentStatus?: (key: string) => boolean | undefined,
    runIoEqualize: () => void,
    grecaptcha: {
      ready(callback: () => void): Promise<unknown>,
      execute(optWidgetId: number, options: { action: string }): Promise<string>,
    },
    UC_UI: undefined | {
      showSecondLayer(): void,
      getSettingsLabels(): any,
      acceptService(key: string): void,
    },
    animateScrollTo: (to: number, duration?: number) => Promise<boolean>,
  }
}

window.runIoEqualize = () => {
  Equalize.run();
};

const ioApp = createApp({
  components: {
    BaseHeader,
    AppFooter,
    Enlargement,
  },
  setup() {
    const isLoading = computed(() => store.getters['page/status'] === 1);
    const isOrderPage = computed(() => store.getters['page/pageType'] === 'order');
    const orderPageFetched = computed(() => store.getters['page/fetched'] === 1);
    const showEnlargement = computed(() => store.getters['enlargement/show']);
    const isMobileBreakpoint = ref<boolean>(true);
    provide('isMobileBreakpoint', isMobileBreakpoint);
    const currentBreakpoints = ref<string[]>([]);
    provide('currentBreakpoints', currentBreakpoints);
    provide('supportsWebP', canUseWebP());

    onBeforeMount(() => {
      store.dispatch('cart/fetch');
      store.dispatch('categories/fetch');
      store.dispatch('branches/fetch');
    });

    const runEqualizer = () => {
      nextTick(() => {
        window.runIoEqualize();
      });
    };

    onMounted(() => {
      window.addEventListener('resize', runEqualizer);
      window.addEventListener('load', runEqualizer);
      runEqualizer();

      initCustomBreakpointHandler();
      onBreakpointChanged((event: CustomEvent) => {
        currentBreakpoints.value = event.detail?.breakpoints || [];
        isMobileBreakpoint.value = !!event.detail?.isMobile;
      }, true);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', runEqualizer);
      window.removeEventListener('load', runEqualizer);
    });

    return {
      isLoading,
      isOrderPage,
      orderPageFetched,
      showEnlargement,
      isMobileBreakpoint,
    };
  },
})
  .directive('inline-html', (element) => {
    if (!element) {
      return;
    }

    const content = element.tagName === 'TEMPLATE' ? element.content : element;
    if (content.children.length === 1) {
      [...element.attributes].forEach((attr) => content.firstChild.setAttribute(attr.name, attr.value));
    }

    if (element.tagName === 'TEMPLATE') {
      element.replaceWith(element.content);
    } else {
      element.replaceWith(...element.children);
    }
  })
  .component('BaseGridRow', BaseGridRow)
  .component('BaseGridCol', BaseGridCol)
  .component('BaseGridWrap', BaseGridWrap)
  .component('BaseLoader', BaseLoader)
  .component('IoHeadline', IoHeadline)
  .component('IoHero', IoHero)
  .component('IoRouterLink', IoRouterLink)
  .component('LazyBackgroundMedia', LazyBackgroundMedia)
  .component('FontAwesomeIcon', FontAwesomeIcon)
  .component('IoSection', IoSection)
  .component('IoContainer', IoContainer)
  .component('IoText', IoText)

  .component('MediaSection', MediaSection)
  .component('BaseButton', BaseButton)

  .component('BaseSpacer', BaseSpacer)
  .component('IoImageSpacer', IoImageSpacer)

  .component('IoCard', IoCard)
  .component('IoMediaCard', IoMediaCard)
  .component('BlogCard', BlogCard)

  .component('IoResponsiveImage', IoResponsiveImage)

  .component('IoFaqs', IoFaqs)
  .component('IoList', IoList)
  .component('IoTooltip', IoTooltip)
  .component('IoCustomerFeedback', IoCustomerFeedback)

  .component('PowerLabelings', PowerLabelings)
  .component('BaseRating', BaseRating)

  .component('BaseEqualizer', BaseEqualizer)
  .component('BaseEqualizerElement', BaseEqualizerElement)
  .component('BaseEqualizerElementGroup', BaseEqualizerElementGroup)
  .component('BaseEqualizerElementPart', BaseEqualizerElementPart)

  .component('BaseTabs', BaseTabs)
  .component('BaseTabsTab', BaseTabsTab)

  .component('MediaCardSlide', MediaCardSlide)
  .component('MediaCardSlider', MediaCardSlider)
  .component('BlogSlider', BlogSlider)
  .component('CardSlider', CardSlider)
  .component('ProductOverviewSlider', ProductOverviewSlider)

  .component('CompanyCertificates', CompanyCertificates)
  .component('BlogOverview', BlogOverview)
  .component('JobOverview', JobOverview)

  .component('Disruptor', Disruptor)
  .component('PageBanner', PageBanner)

  .component('HeroSlider', HeroSlider)
  .component('HeroSlide', HeroSlide)

  .component('CircledIcon', CircledIcon)

  .component('ProcessLine', ProcessLine)
  .component('ProcessItem', ProcessItem)

  .component('BaseCalculator', BaseCalculator)

  .component('StepAddress', StepAddress)
  .component('StepConnection', StepConnection)
  .component('StepPayment', StepPayment)
  .component('StepOverview', StepOverview)
  .component('InputSection', InputSection)

  .component('BaseFixedAside', BaseFixedAside)
  .component('ScrollTopButton', ScrollTopButton)

  .component('IoCardContact', IoCardContact)
  .component('IoFormContactCallback', IoFormContactCallback)
  .component('IoFormContactEmail', IoFormContactEmail)

  .component('FaIcon', FaIcon)
  .component('IconAndroid', IconAndroid)
  .component('IconApple', IconApple)
  .component('IconCallback', IconCallback)
  .component('IconFacebook', IconFacebook)
  .component('IconInstagram', IconInstagram)
  .component('IconLiveChat', IconLiveChat)
  .component('IconTwitter', IconTwitter)
  .component('IconVideoConsulting', IconVideoConsulting)
  .component('IconWhatsapp', IconWhatsapp)
  .component('IconYoutube', IconYoutube)
  .component('IconTree', IconTree)
  .component('IconContact', IconContact)
  .component('IconServiceApp', IconServiceApp)

  .component('IoWave', IoWave)
  .component('IconWave', IconWave)
  .component('IconWaveFlip', IconWaveFlip)
  .component('IconWaveNegativ', IconWaveNegativ)
  .component('IconWaveNegativFlip', IconWaveNegativFlip)
  .component('IconWaveDouble', IconWaveDouble)
  .component('IconWaveDoubleFlip', IconWaveDoubleFlip)
  .component('IconWaveDoubleNegativ', IconWaveDoubleNegativ)
  .component('IconWaveDoubleNegativFlip', IconWaveDoubleNegativFlip)
  .component('IconWaveDoubleCard', IconWaveDoubleCard)

  .component('FormContractCancellation', FormContractCancellation)
  .component('FormNewsletter', FormNewsletter)
  .component('FormRelocation', FormRelocation)
  .component('FormSponsoring', FormSponsoring)

  .component('SectionNewsletter', SectionNewsletter)

  .component('IframeFacade', IframeFacade)

  .component('PageError', PageError)

  .use(store)
  .use(router);

ioApp.mount('#ioapp');
